import { baseApi } from "./baseApi";

// Types
export interface FAQCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  categoryId: string;
  category?: FAQCategory;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FAQCategoryWithFAQs extends FAQCategory {
  faqs: FAQ[];
}

export interface CreateFAQCategoryData {
  name: string;
  slug: string;
  description?: string;
  order?: number;
  isActive?: boolean;
}

export interface UpdateFAQCategoryData {
  name?: string;
  slug?: string;
  description?: string;
  order?: number;
  isActive?: boolean;
}

export interface CreateFAQData {
  question: string;
  answer: string;
  categoryId: string;
  order?: number;
  isActive?: boolean;
}

export interface UpdateFAQData {
  question?: string;
  answer?: string;
  categoryId?: string;
  order?: number;
  isActive?: boolean;
}

export interface ReorderData {
  items: { id: string; order: number }[];
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface FAQCategoryFilters {
  page?: number;
  limit?: number;
}

export interface FAQFilters {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedApiResponse<T> {
  success: boolean;
  data: T;
  pagination: PaginationMeta;
  message?: string;
}

/**
 * FAQ API slice
 * Handles all FAQ-related API calls
 */
export const faqApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ==================== PUBLIC ENDPOINTS ====================
    
    // Get all active FAQs grouped by category (public)
    getPublicFAQs: builder.query<ApiResponse<FAQCategoryWithFAQs[]>, void>({
      query: () => "/faqs",
      providesTags: ["FAQ", "FAQCategory"],
    }),

    // ==================== ADMIN: CATEGORIES ====================

    // Get all FAQ categories (admin) with pagination
    getFAQCategories: builder.query<PaginatedApiResponse<FAQCategory[]>, FAQCategoryFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return `/faqs/categories${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["FAQCategory"],
    }),

    // Get single FAQ category by ID
    getFAQCategoryById: builder.query<ApiResponse<FAQCategoryWithFAQs>, string>({
      query: (id) => `/faqs/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: "FAQCategory", id }],
    }),

    // Create FAQ category
    createFAQCategory: builder.mutation<ApiResponse<FAQCategory>, CreateFAQCategoryData>({
      query: (data) => ({
        url: "/faqs/categories",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["FAQCategory", "FAQ"],
    }),

    // Update FAQ category
    updateFAQCategory: builder.mutation<ApiResponse<FAQCategory>, { id: string; data: UpdateFAQCategoryData }>({
      query: ({ id, data }) => ({
        url: `/faqs/categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "FAQCategory", id },
        "FAQCategory",
        "FAQ",
      ],
    }),

    // Delete FAQ category
    deleteFAQCategory: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/faqs/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FAQCategory", "FAQ"],
    }),

    // Reorder FAQ categories
    reorderFAQCategories: builder.mutation<ApiResponse<void>, ReorderData>({
      query: (data) => ({
        url: "/faqs/categories/reorder",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["FAQCategory", "FAQ"],
    }),

    // ==================== ADMIN: FAQs ====================

    // Get all FAQs (admin - includes inactive) with pagination
    getAllFAQs: builder.query<PaginatedApiResponse<FAQ[]>, FAQFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.search) searchParams.append("search", params.search);
        if (params?.categoryId) searchParams.append("categoryId", params.categoryId);
        const queryString = searchParams.toString();
        return `/faqs/admin${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["FAQ"],
    }),

    // Get single FAQ by ID
    getFAQById: builder.query<ApiResponse<FAQ>, string>({
      query: (id) => `/faqs/admin/${id}`,
      providesTags: (_result, _error, id) => [{ type: "FAQ", id }],
    }),

    // Get FAQs by category
    getFAQsByCategory: builder.query<ApiResponse<FAQ[]>, string>({
      query: (categoryId) => `/faqs/admin/category/${categoryId}`,
      providesTags: (_result, _error, categoryId) => [
        { type: "FAQ", id: `category-${categoryId}` },
        "FAQ",
      ],
    }),

    // Create FAQ
    createFAQ: builder.mutation<ApiResponse<FAQ>, CreateFAQData>({
      query: (data) => ({
        url: "/faqs/admin",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["FAQ", "FAQCategory"],
    }),

    // Update FAQ
    updateFAQ: builder.mutation<ApiResponse<FAQ>, { id: string; data: UpdateFAQData }>({
      query: ({ id, data }) => ({
        url: `/faqs/admin/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "FAQ", id },
        "FAQ",
        "FAQCategory",
      ],
    }),

    // Delete FAQ
    deleteFAQ: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/faqs/admin/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FAQ", "FAQCategory"],
    }),

    // Reorder FAQs
    reorderFAQs: builder.mutation<ApiResponse<void>, ReorderData>({
      query: (data) => ({
        url: "/faqs/admin/reorder",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["FAQ"],
    }),
  }),
});

export const {
  // Public
  useGetPublicFAQsQuery,
  // Admin: Categories
  useGetFAQCategoriesQuery,
  useGetFAQCategoryByIdQuery,
  useCreateFAQCategoryMutation,
  useUpdateFAQCategoryMutation,
  useDeleteFAQCategoryMutation,
  useReorderFAQCategoriesMutation,
  // Admin: FAQs
  useGetAllFAQsQuery,
  useGetFAQByIdQuery,
  useGetFAQsByCategoryQuery,
  useCreateFAQMutation,
  useUpdateFAQMutation,
  useDeleteFAQMutation,
  useReorderFAQsMutation,
} = faqApi;
